import styled from '@emotion/styled';
import { useUser } from 'common/CacheProvider';
import { globalPadding } from 'common/Layout/Layout';
import { IconClose } from 'common/icons';
import EllipsisText from 'common/styles/EllipsisText';
import withStyleSystem from '../withStyleSystem';
import { ColorKey } from '../theme';
import { popUpMessaging } from 'common/apiService';
import { useCallback, useEffect, useRef, useState } from 'react';
import lscache from 'lscache';
import { useRouter } from 'next/router';
import useIsomorphicLayoutEffect from 'common/utils/useIsomorphicLayoutEffect';

const MessageWrapper = styled.div<{ type?: string }>(({ theme, type }) => ({
  background: type && type === 'error' ? theme.palette.warning[100][0] : theme.palette.success[300][0],
  padding: theme.spacing(2, 0),
  color: theme.palette.text[900][0],
  ...theme.mixins.create(globalPadding, (p) => ({ padding: `12px ${theme.spacing(p)}` })),
  height: '48px',
  position: 'fixed',
  width: '100%',
  top: 0,
  fontSize: '0.875rem',
  zIndex: theme.zIndex.systemMessage,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'start',
  [theme.breakpoints.down('md')]: {
    height: '72px',
  },
}));

const IconWrapper = withStyleSystem(styled.div<{ color?: ColorKey }>(({ theme, color }) => ({
  cursor: 'pointer',
  color: color ? theme.palette[color][900][0] : undefined,
  verticalAlign: 'middle',
  width: '20px',
  height: '25px',
  marginLeft: '0.5rem',
  [theme.breakpoints.down('xs')]: {
    height: '28px',
    width: '35px',
  },
})));

export interface SystemMessage {
  message: string
  isClosedBefore: boolean
  is_dismissable: boolean
  type: string
}

const SystemMessage = () => {
  const [system_message, setSystemMessage] = useState<SystemMessage | null>(null);
  const [maxLine, setMaxLine] = useState(1);
  const { loaded } = useUser();
  const router = useRouter();

  const handleGetPopUpMessage = async () => {
    await popUpMessaging().then(({ data }) => {
      if (data) {
        if (!system_message || data?.text !== system_message?.message || !system_message?.isClosedBefore) {
          lscache.set('system_message', {
            message: data?.text,
            isClosedBefore: false,
            is_dismissable: data.is_dismissable,
            type: data?.type,
          });
        }
      } else {
        if (system_message) {
          lscache.set('system_message', null);
        }
      }
    }).catch(() => {
      lscache.set('system_message', null);
    });
  };

  // Runs before first paint — prevents CLS from lscache read causing layout shift
  useIsomorphicLayoutEffect(() => {
    setSystemMessage(lscache.get('system_message'));
    setMaxLine(window.innerWidth < 960 ? 2 : 1);
  }, []);

  const handleStorageChange = useCallback(() => {
    setSystemMessage(lscache.get('system_message'));
  }, []);

  useEffect(() => {
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [handleStorageChange]);

  useEffect(() => {
    loaded && handleGetPopUpMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  // Throttled resize handler — avoids reading innerWidth on every render
  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleResize = useCallback(() => {
    if (resizeTimerRef.current !== null) return;
    resizeTimerRef.current = setTimeout(() => {
      setMaxLine(window.innerWidth < 960 ? 2 : 1);
      resizeTimerRef.current = null;
    }, 100);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
    };
  }, [handleResize]);

  if (!system_message?.message || system_message?.isClosedBefore || router.asPath.includes('play')) return <></>;
  return <MessageWrapper type={system_message?.type}>
    {system_message.is_dismissable && <IconWrapper onClick={() => {
      lscache.set('system_message', { ...system_message, isClosedBefore: true });
      window.dispatchEvent(new Event('storage'));
    }}>
      <IconClose /></IconWrapper>}
    <EllipsisText color='black' line={maxLine}>{system_message?.message}</EllipsisText>
  </MessageWrapper>;
};

export default SystemMessage;
